import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import { newId } from "../model/ids";
import { withDecoration } from "../model/projectOps";
import { pathFromBuffer, setPathPoints } from "../canvas/Scene";
import { cssVar } from "../colors";
import type { FreePathDecoration } from "../model/Project";

/**
 * Freehand swivel-knife / lining tool. Pen pressure modulates stroke width.
 */
export class FreePathTool extends ToolBase {
  id = "freePath";
  label = "Line";
  shortcut = "p";
  cursor = "crosshair";

  private buffer: { x: number; y: number }[] = [];
  private preview: paper.Path | null = null;
  private maxPressure = 1;

  override onDown(s: PointerSample, p: { x: number; y: number }) {
    this.buffer = [{ x: p.x, y: p.y }];
    this.maxPressure = s.pressure;
    this.startPreview(s.pressure);
  }

  override onMove(s: PointerSample, p: { x: number; y: number }) {
    if (s.pressure > this.maxPressure) this.maxPressure = s.pressure;
    const last = this.buffer[this.buffer.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 0.8) {
      this.buffer.push({ x: p.x, y: p.y });
      this.updatePreview();
    }
  }

  override onUp(_s: PointerSample, _p: { x: number; y: number }) {
    if (this.buffer.length < 2) {
      this.cleanup();
      return;
    }
    const data = pathFromBuffer(this.buffer);
    const scope = this.ctx.scene.getPaperScope();
    const tmp = new scope.CompoundPath(data);
    const center = tmp.bounds.center;
    tmp.remove();
    const hit = this.ctx.scene.hitTest(center);
    const pieceId =
      hit.pieceId ??
      this.ctx.scene.getSelection().pieceId ??
      this.ctx.history.state.pieces[0]?.id;
    this.cleanup();
    if (!pieceId) return;
    const strokeWidth = Math.max(
      0.4,
      this.ctx.settings.brushSize * this.maxPressure,
    );
    const dec: FreePathDecoration = {
      id: newId("dec"),
      kind: "freePath",
      name: "Line",
      visible: true,
      depth: -0.4,
      pathData: data,
      strokeWidth,
      closed: false,
    };
    this.ctx.commit("Draw line", (proj) => withDecoration(proj, pieceId, dec));
  }

  override onCancel() {
    this.cleanup();
  }

  private startPreview(pressure: number) {
    const scope = this.ctx.scene.getPaperScope();
    this.preview = new scope.Path();
    this.preview.strokeColor = new scope.Color(cssVar("--preview-freehand"));
    this.preview.strokeWidth = Math.max(
      0.4,
      this.ctx.settings.brushSize * pressure,
    );
    this.preview.fillColor = null;
    this.preview.data = { ephemeral: true };
  }

  private updatePreview() {
    if (!this.preview) return;
    const scope = this.ctx.scene.getPaperScope();
    this.preview.removeSegments();
    setPathPoints(
      this.preview,
      this.buffer.map((p) => new scope.Point(p.x, p.y)),
    );
  }

  private cleanup() {
    if (this.preview) {
      this.preview.remove();
      this.preview = null;
    }
    this.buffer = [];
  }
}
